import {useCallback, useEffect, useRef, useState} from "react";

import {useGetHomematchPreleadsQuery} from "../api/get_homematch_preleads";
import {
    IHomematchLeadRequirementsError,
    IRejectHomematchPreleadPayload,
    usePatchAcceptHomematchPreleadMutation,
    usePatchMarkHomematchPreleadShownMutation,
    usePatchRejectHomematchPreleadMutation
} from "../api/homematch_prelead_actions";
import {HomematchPreleadStatus, IHomematchPreleadListItem} from "../types/IHomematchPrelead";

interface IParams {
    /** The deck reports cards as shown and reacts to swipes only while the modal is open. */
    isOpen: boolean;
}

export interface IHomematchTinderDeckResult {
    currentPrelead: IHomematchPreleadListItem | null;
    /** Pending cards left to swipe, the current one included — feeds the offer counter. */
    queue: IHomematchPreleadListItem[];
    /** Rendered behind the current card so a swipe reveals it instantly. */
    upcomingPrelead: IHomematchPreleadListItem | null;
    /** Up to three ids behind the current card, for the deck shadow stack. */
    shadowPreleadIds: number[];
    remainingCount: number;
    isLoading: boolean;
    isSwiping: boolean;
    /** Missing account data reported by a 409 on accept; null otherwise. */
    leadRequirementsError: IHomematchLeadRequirementsError | null;
    acceptCurrentPrelead: () => Promise<boolean>;
    rejectCurrentPrelead: (params: Omit<IRejectHomematchPreleadPayload, "preleadId">) => Promise<boolean>;
    clearLeadRequirementsError: () => void;
}

/**
 * Queue over the shared prelead list. Swipes go through the API and
 * the card is dropped locally right away — the invalidated list refetch then
 * brings whatever the backend refilled into the buffer. Cards that turn out to
 * be stale (decided from another portal meanwhile — 400/404) are dropped the
 * same way instead of blocking the deck.
 */
export const useHomematchTinderDeck = (params: IParams): IHomematchTinderDeckResult => {
    const {data, isLoading} = useGetHomematchPreleadsQuery();
    const [decidedPreleadIds, setDecidedPreleadIds] = useState<number[]>([]);
    const [leadRequirementsError, setLeadRequirementsError] = useState<IHomematchLeadRequirementsError | null>(null);
    const shownPreleadIdsRef = useRef<Set<number>>(new Set());
    const [acceptPrelead, {isLoading: isAccepting}] = usePatchAcceptHomematchPreleadMutation();
    const [rejectPrelead, {isLoading: isRejecting}] = usePatchRejectHomematchPreleadMutation();
    const [markPreleadShown] = usePatchMarkHomematchPreleadShownMutation();

    const queue = (data?.response?.results ?? []).filter(
        (prelead) => prelead.status === HomematchPreleadStatus.PENDING && !decidedPreleadIds.includes(prelead.id)
    );
    const currentPrelead = queue[0] ?? null;
    const currentPreleadId = currentPrelead?.id ?? null;

    useEffect(() => {
        if (!params.isOpen || currentPreleadId === null || shownPreleadIdsRef.current.has(currentPreleadId)) {
            return;
        }

        shownPreleadIdsRef.current.add(currentPreleadId);
        // Fire-and-forget: a failed impression report must not affect the deck.
        markPreleadShown({preleadId: currentPreleadId});
    }, [params.isOpen, currentPreleadId, markPreleadShown]);

    const dropCurrentPrelead = useCallback(() => {
        if (currentPreleadId !== null) {
            setDecidedPreleadIds((decidedIds) => [...decidedIds, currentPreleadId]);
        }
    }, [currentPreleadId]);

    const acceptCurrentPrelead = useCallback(async (): Promise<boolean> => {
        if (currentPreleadId === null) {
            return false;
        }

        try {
            await acceptPrelead({preleadId: currentPreleadId}).unwrap();
            dropCurrentPrelead();

            return true;
        } catch (error) {
            const status = (error as {status?: number}).status;

            if (status === 409) {
                setLeadRequirementsError((error as {data?: IHomematchLeadRequirementsError}).data ?? null);

                return false;
            }

            // 400/404: the card left the swipeable set (decided elsewhere, converted) — drop it and move on.
            dropCurrentPrelead();

            return false;
        }
    }, [acceptPrelead, currentPreleadId, dropCurrentPrelead]);

    const rejectCurrentPrelead = useCallback(
        async (rejectParams: Omit<IRejectHomematchPreleadPayload, "preleadId">): Promise<boolean> => {
            if (currentPreleadId === null) {
                return false;
            }

            try {
                await rejectPrelead({preleadId: currentPreleadId, ...rejectParams}).unwrap();
                dropCurrentPrelead();

                return true;
            } catch {
                dropCurrentPrelead();

                return false;
            }
        },
        [rejectPrelead, currentPreleadId, dropCurrentPrelead]
    );

    return {
        currentPrelead,
        queue,
        upcomingPrelead: queue[1] ?? null,
        shadowPreleadIds: queue.slice(1, 4).map((prelead) => prelead.id),
        remainingCount: queue.length,
        isLoading,
        isSwiping: isAccepting || isRejecting,
        leadRequirementsError,
        acceptCurrentPrelead,
        rejectCurrentPrelead,
        clearLeadRequirementsError: () => setLeadRequirementsError(null)
    };
};
import {useCallback, useEffect, useRef, useState} from "react";

import {useGetHomematchPreleadsQuery} from "../api/get_homematch_preleads";
import {
    IHomematchLeadRequirementsError,
    IRejectHomematchPreleadPayload,
    usePatchAcceptHomematchPreleadMutation,
    usePatchMarkHomematchPreleadShownMutation,
    usePatchRejectHomematchPreleadMutation
} from "../api/homematch_prelead_actions";
import {HomematchPreleadStatus, IHomematchPreleadListItem} from "../types/IHomematchPrelead";

interface IParams {
    /** The deck reports cards as shown and reacts to swipes only while the modal is open. */
    isOpen: boolean;
}

export interface IHomematchTinderDeckResult {
    currentPrelead: IHomematchPreleadListItem | null;
    /** Pending cards left to swipe, the current one included — feeds the offer counter. */
    queue: IHomematchPreleadListItem[];
    /** Rendered behind the current card so a swipe reveals it instantly. */
    upcomingPrelead: IHomematchPreleadListItem | null;
    /** Up to three ids behind the current card, for the deck shadow stack. */
    shadowPreleadIds: number[];
    remainingCount: number;
    isLoading: boolean;
    isSwiping: boolean;
    /** Missing account data reported by a 409 on accept; null otherwise. */
    leadRequirementsError: IHomematchLeadRequirementsError | null;
    acceptCurrentPrelead: () => Promise<boolean>;
    rejectCurrentPrelead: (params: Omit<IRejectHomematchPreleadPayload, "preleadId">) => Promise<boolean>;
    clearLeadRequirementsError: () => void;
}

/**
 * Queue over the shared prelead list. Swipes go through the API and
 * the card is dropped locally right away — the invalidated list refetch then
 * brings whatever the backend refilled into the buffer. Cards that turn out to
 * be stale (decided from another portal meanwhile — 400/404) are dropped the
 * same way instead of blocking the deck.
 */
export const useHomematchTinderDeck = (params: IParams): IHomematchTinderDeckResult => {
    const {data, isLoading} = useGetHomematchPreleadsQuery();
    const [decidedPreleadIds, setDecidedPreleadIds] = useState<number[]>([]);
    const [leadRequirementsError, setLeadRequirementsError] = useState<IHomematchLeadRequirementsError | null>(null);
    const shownPreleadIdsRef = useRef<Set<number>>(new Set());
    const [acceptPrelead, {isLoading: isAccepting}] = usePatchAcceptHomematchPreleadMutation();
    const [rejectPrelead, {isLoading: isRejecting}] = usePatchRejectHomematchPreleadMutation();
    const [markPreleadShown] = usePatchMarkHomematchPreleadShownMutation();

    const queue = (data?.response?.results ?? []).filter(
        (prelead) => prelead.status === HomematchPreleadStatus.PENDING && !decidedPreleadIds.includes(prelead.id)
    );
    const currentPrelead = queue[0] ?? null;
    const currentPreleadId = currentPrelead?.id ?? null;

    useEffect(() => {
        if (!params.isOpen || currentPreleadId === null || shownPreleadIdsRef.current.has(currentPreleadId)) {
            return;
        }

        shownPreleadIdsRef.current.add(currentPreleadId);
        // Fire-and-forget: a failed impression report must not affect the deck.
        markPreleadShown({preleadId: currentPreleadId});
    }, [params.isOpen, currentPreleadId, markPreleadShown]);

    const dropCurrentPrelead = useCallback(() => {
        if (currentPreleadId !== null) {
            setDecidedPreleadIds((decidedIds) => [...decidedIds, currentPreleadId]);
        }
    }, [currentPreleadId]);

    const acceptCurrentPrelead = useCallback(async (): Promise<boolean> => {
        if (currentPreleadId === null) {
            return false;
        }

        try {
            await acceptPrelead({preleadId: currentPreleadId}).unwrap();
            dropCurrentPrelead();

            return true;
        } catch (error) {
            const status = (error as {status?: number}).status;

            if (status === 409) {
                setLeadRequirementsError((error as {data?: IHomematchLeadRequirementsError}).data ?? null);

                return false;
            }

            // 400/404: the card left the swipeable set (decided elsewhere, converted) — drop it and move on.
            dropCurrentPrelead();

            return false;
        }
    }, [acceptPrelead, currentPreleadId, dropCurrentPrelead]);

    const rejectCurrentPrelead = useCallback(
        async (rejectParams: Omit<IRejectHomematchPreleadPayload, "preleadId">): Promise<boolean> => {
            if (currentPreleadId === null) {
                return false;
            }

            try {
                await rejectPrelead({preleadId: currentPreleadId, ...rejectParams}).unwrap();
                dropCurrentPrelead();

                return true;
            } catch {
                dropCurrentPrelead();

                return false;
            }
        },
        [rejectPrelead, currentPreleadId, dropCurrentPrelead]
    );

    return {
        currentPrelead,
        queue,
        upcomingPrelead: queue[1] ?? null,
        shadowPreleadIds: queue.slice(1, 4).map((prelead) => prelead.id),
        remainingCount: queue.length,
        isLoading,
        isSwiping: isAccepting || isRejecting,
        leadRequirementsError,
        acceptCurrentPrelead,
        rejectCurrentPrelead,
        clearLeadRequirementsError: () => setLeadRequirementsError(null)
    };
};
